/**
 * O CATÁLOGO DE VERSÕES do Sistema CD (14/08, pedido do Lucas).
 *
 * COMO O NÚMERO FUNCIONA — `v1.3.12`:
 *   1  → a geração do sistema. O 1 nasceu na virada de chave (06/08/2026),
 *        quando o CD saiu da planilha para o sistema próprio.
 *   3  → sobe a cada lote PROMOVIDO À PRODUÇÃO (o botão 🚀 do sandbox).
 *   12 → sobe a cada SUBIDA AO SANDBOX. É o número que anda todo dia.
 *
 * Ou seja: o meio conta quantas vezes os funcionários receberam novidade; o
 * fim conta quantas vezes o Lucas pôde testar antes disso.
 *
 * REGRA DE TRABALHO: toda subida ao sandbox acrescenta uma entrada NOVA aqui,
 * no topo, escrita para quem usa o sistema — não para quem programa. Quando o
 * lote vai para produção, a entrada da promoção marca `ambiente: 'producao'`.
 *
 * O Painel mostra o resumo da última versão; o link "Ver histórico de
 * atualizações" abre esta lista inteira.
 */

export interface Versao {
  /** Sempre no formato v<geração>.<produção>.<sandbox>. */
  versao: string;
  /** AAAA-MM-DD. */
  data: string;
  /** Onde esta versão está: no sandbox (teste) ou já com os funcionários. */
  ambiente: 'sandbox' | 'producao';
  /** Uma linha por mudança, em português de usuário. */
  itens: string[];
}

/** Da mais nova para a mais antiga — é a ordem em que se lê um histórico. */
export const VERSOES: Versao[] = [
  {
    versao: 'v1.4.22',
    data: '2026-11-03',
    ambiente: 'producao',
    itens: [
      '🦺 Segurança do Trabalho ▸ ficha do funcionário: clique no nome (em Funcionários e ASO ou em Pendências) e veja tudo dele numa tela: semáforo de ASO, treinamentos, EPI e pendências; os ASOs com os exames; toxicológico, vacinas, afastamentos e CAT; o que a lei e o PGR exigem de treinamento e EPI para a função; ordens de serviço; as obras por período; a pasta de documentos; e a linha do tempo.',
      'Pasta de documentos por funcionário: contrato, CTPS, termos, cartão de vacina, rescisão e o papel digitalizado de certificado, OS e ficha de EPI entram pela própria ficha (PDF ou foto, com tipo e data) ou por e-mail para sistema@ademaldo.com.br — com o CPF legível no documento, o sistema guarda sozinho na pasta certa; sem CPF, fica pendente na Caixa de entrada para alguém dizer de quem é.',
      'Funcionários e ASO: a busca de cima continua só nos ativos; embaixo, "Buscar no cadastro completo" acha também quem já saiu, para abrir a ficha de ex-funcionário (o prontuário fica guardado 20 anos).',
      'Quem tem o módulo Segurança do Trabalho vê os dados de saúde da ficha (decisão de 03/11). Os formulários de registrar certificado, entrega de EPI, vacina, OS, CAT e toxicológico são a próxima rodada: por enquanto essas seções mostram o que as cargas trazem e, se nada, dizem isso.',
    ],
  },
  {
    versao: 'v1.4.21',
    data: '2026-10-29',
    ambiente: 'producao',
    itens: [
      'Abastecimento: faltar a requisição (foto ou número) ou a nota fiscal não trava mais o envio. O sistema mostra o que falta e libera "📨 Enviar mesmo assim"; o registro entra na revisão da Administração, que é avisada na hora.',
      'Abastecimento: número de requisição que já foi usado também deixou de trancar (a leitura do número escrito à mão pode errar um algarismo). Enviado mesmo assim, ele vai para a revisão sem o número, e a Administração confere na foto e corrige lá.',
    ],
  },
  {
    versao: 'v1.4.20',
    data: '2026-10-29',
    ambiente: 'producao',
    itens: [
      'Pedidos ▸ Prioridade de saída: cada cartão mostra a contagem ("Vence em 12 dias", "VENCE HOJE", "VENCIDO HÁ 3 DIAS"). O lote sai do quadro quando faz 60 dias vencido, quando o item zera no estoque ou quando é descartado em Validades.',
      'Lote descartado em Validades não aparece mais como vencido em Pedidos nem no Estoque. Antes ele continuava lá se o item tivesse saldo de outro lote (caso do Selador pig 16L 15BD).',
    ],
  },
  {
    versao: 'v1.4.19',
    data: '2026-10-29',
    ambiente: 'producao',
    itens: [
      'O "📄 abrir o arquivo" da Caixa de entrada abre a nota ou a imagem recebida, em vez da mensagem "Faça login para continuar". O mesmo conserto vale para o PDF da nota de oficina na Frota e para qualquer link de documento do sistema.',
    ],
  },
  {
    versao: 'v1.4.17',
    data: '2026-10-27',
    ambiente: 'producao',
    itens: [
      'Os e-mails do sistema (acesso, aviso de pedido, relatórios, vigia) passam a sair de sistema@ademaldo.com.br, e não mais da conta do Lucas. Quem responder a um deles fala com o Lucas.',
      'Os links dos e-mails voltam para sistema-cd-ademaldo.web.app: o Gmail recusou os e-mails de acesso com o endereço novo, por ser recente demais. O sistema.ademaldo.com.br continua funcionando para quem digitar.',
    ],
  },
  {
    versao: 'v1.4.16',
    data: '2026-10-27',
    ambiente: 'producao',
    itens: [
      'O sistema ganhou endereço próprio: sistema.ademaldo.com.br. O e-mail de acesso, o aviso de pedido novo e os relatórios por e-mail passam a levar para ele.',
      'O endereço antigo (sistema-cd-ademaldo.web.app) continua funcionando. Quem trocar de endereço entra de novo uma vez e, se usa o ícone no celular ou as notificações, instala e libera de novo pelo endereço novo.',
    ],
  },
  {
    versao: 'v1.4.15',
    data: '2026-10-27',
    ambiente: 'producao',
    itens: [
      'Caixa de entrada do sistema: documentos mandados para sistema@ademaldo.com.br são lidos sozinhos a cada 10 minutos. A IA diz o que é cada anexo e registra quando não há dúvida.',
      'ASO da ASMETRO entra direto na Segurança do Trabalho, casado pelo CPF com o cadastro da folha: resultado, validade, aptidões (altura, espaço confinado, eletricidade…), exames complementares e médico, com o arquivo guardado.',
      'Nota de oficina encaminhada vira manutenção do veículo, se a placa estiver na nota ou no assunto do e-mail e quem mandou tiver a Administração.',
      'Qualquer dúvida (CPF que não casa, marca ilegível, ASO inapto, placa que não aparece, remetente não conferido) deixa o documento pendente em Administração ▸ 📨 Caixa de entrada, com a dúvida escrita e os campos para confirmar ou descartar.',
      'Só lê e-mail de quem está no cadastro de Pessoas ou de parceiro liberado (a ASMETRO já está). O resto é ignorado sem gastar IA.',
    ],
  },
  {
    versao: 'v1.4.14',
    data: '2026-09-18',
    ambiente: 'producao',
    itens: [
      'Usuário master virou uma opção própria no cadastro de Pessoas (Administração ▸ Cadastros ▸ Pessoas ▸ abrir a pessoa ▸ "👑 Esta pessoa é master"). Ter o módulo Administração não torna ninguém master.',
      'Só um master torna outra pessoa master, e só um master altera os próprios módulos. Quem não é master continua sem poder mudar o próprio acesso. O sistema nunca fica sem nenhum master ativo.',
      'Na lista de Pessoas, quem é master aparece com a marca 👑.',
    ],
  },
  {
    versao: 'v1.4.13',
    data: '2026-09-18',
    ambiente: 'producao',
    itens: [
      '🦺 Segurança do Trabalho ▸ nova aba "Documentos e lei". Os documentos da empresa (PGR, PCMSO, LTCAT, AEP) foram auditados em 10/09 e têm ressalvas: os 53 achados estão lá, com a norma, o que pedir ao fornecedor e o que o sistema faz enquanto isso.',
      'Regra em vigor (decisão do Lucas, 18/09): a lei prevalece sobre os documentos até a revisão chegar. Onde o documento é mais rigoroso, vale o documento. Tudo o que o sistema tirou dos documentos auditados (212 exigências de exame, os GHEs e as 4 linhas de treinamento do PGR) está marcado para reanálise quando os documentos novos chegarem.',
      'Matriz legal de treinamentos: o PGR só tem 4; entram com procedência "norma" a integração de obra (NR-18), NR-33, NR-10 e NR-12, além da NR-35. Quem está exposto é decidido pela lei primeiro: se o risco está no inventário do GHE, todos do GHE contam.',
      'Alertas de documento: PGR com revisão em 31/10/2026 e o PCMSO, que vale até 2027, depende dele.',
    ],
  },
  {
    versao: 'v1.4.12',
    data: '2026-09-18',
    ambiente: 'producao',
    itens: [
      'Módulo novo: 🦺 Segurança do Trabalho (para quem tiver o módulo liberado em Administração ▸ Pessoas). O cadastro vem sozinho da folha (Domínio, todo dia 12:00) e os exames vêm sozinhos da clínica (portal da ASMETRO, todo dia 06:30). Ninguém digita nada.',
      'A tela mostra quem precisa de quê: ASO vencido ou vencendo em 30 dias, quem voltou de afastamento de 30 dias ou mais sem exame de retorno, toxicológico dos motoristas e demissional pendente, separados em "Agir agora", "Programar" e "Afastados (exame na volta)". Cada pendência diz a base: NR-7, CLT ou PCMSO.',
      'O portal da clínica não tem CPF: o que não casou por nome idêntico fica na aba "Nomes sem vínculo" até alguém dizer quem é. Nada é adivinhado.',
      'Este módulo vai direto para a produção, sem passar pelo sandbox (decisão do Lucas, 17/09), porque ninguém tem acesso a ele ainda.',
    ],
  },
  {
    versao: 'v1.4.11',
    data: '2026-09-17',
    ambiente: 'producao',
    itens: [
      'Botão 🚀 Escolher o que vai para a produção: mesmo defeito do 🧪, disparava uma publicação por PR mais uma do catálogo, todas ao mesmo tempo, e a produção podia ficar na versão errada (aconteceu em 17/09). Agora monta tudo num ramo de rascunho e publica uma vez só.',
    ],
  },
  {
    versao: 'v1.4.10',
    data: '2026-09-17',
    ambiente: 'producao',
    itens: [
      'Botão 🧪 Montar sandbox: disparava duas publicações ao mesmo tempo e, dependendo de qual terminava por último, a versão sem as mudanças ficava no ar. Agora monta tudo num ramo de rascunho e publica uma vez só; o /saude do sandbox mostra um commit novo a cada remontagem.',
    ],
  },
  {
    versao: 'v1.4.9',
    data: '2026-09-16',
    ambiente: 'producao',
    itens: [
      'Abastecimento em posto não cadastrado: um botão ao lado das fotos ("⛽ Posto não cadastrado") dispensa a requisição — o registro sai só com a nota fiscal, e o motorista informa o centro de custo.',
      'Centro de custo em todo abastecimento: a palavra escrita à mão na requisição (ADM, Rancho, DMI ou o nome de uma obra) é lida com a lista de obras como referência e casada com a obra de verdade; fica no registro, na ficha do veículo, na revisão (editável) e como coluna nos relatórios. Vai servir para os relatórios por centro de custo.',
    ],
  },
  {
    versao: 'v1.4.8',
    data: '2026-09-16',
    ambiente: 'producao',
    itens: [
      'Estoque nunca negativo: o próprio banco recusa qualquer saída maior do que há — romaneio, ajuste ou descarte — com a mensagem "X tem N em estoque e a saída pede M". Era assim que apareciam saldos negativos.',
      'Descarte de lote vencido pergunta quanto foi jogado fora (sugere o que resta, entre o lote e o estoque) e nunca abate mais do que há. Se nada restava, o lote só sai da lista, sem baixa. Foi o caso do Bloco 9×19×39: 60 descartados de um lote que já tinha saído por ajuste.',
      'Separação de pedido e criação de romaneio travam os itens durante a conferência de disponibilidade — dois separadores ao mesmo tempo não comprometem mais do que existe.',
      'Erro inesperado no servidor deixa de mostrar texto técnico: vira uma mensagem simples e fica no log para a Administração.',
    ],
  },
  {
    versao: 'v1.4.7',
    data: '2026-09-15',
    ambiente: 'producao',
    itens: [
      'Aprovação por mudança: a tarja do sandbox agora lista cada mudança aberta com uma caixa de seleção. Só o que for marcado vai para a produção; o resto continua em teste. Antes, aprovar levava tudo o que estava no sandbox de uma vez.',
      'Botão "Montar sandbox": recria o ambiente de teste com a produção mais todas as mudanças abertas, e avisa quais ficaram de fora por conflito.',
      'O histórico de atualizações passa a ser marcado como "em produção" sozinho no momento da aprovação.',
    ],
  },
  {
    versao: 'v1.4.6',
    data: '2026-09-14',
    ambiente: 'producao',
    itens: [
      'Vírgula do hodômetro esquecida: quem digita 2809575 para 280.957,5 km passa a ser entendido — a tela troca na hora ("✂️ Entendi 2.809.575 como 280.957 km"), o servidor grava a km certa com rastro, e o mesmo vale para a km lida nas fotos. Nada de salto improvável nem revisão por causa disso.',
      'Correção de km em cadeia: quando o abastecimento anterior também está sem a vírgula, a correção na ficha do veículo oferece consertar os anteriores junto (do mais novo para o mais antigo), em vez de barrar. Os consumos do veículo são refeitos por inteiro.',
      'PRE3585 corrigido em produção: 02/09 → 280.428 km, 14/09 → 280.957 km (4,86 km/l).',
    ],
  },
  {
    versao: 'v1.4.5',
    data: '2026-09-12',
    ambiente: 'producao',
    itens: [
      'Abastecimento destravado (desde 03/09 nenhum motorista conseguia concluir): o botão "Registrar" espera a leitura da foto terminar em vez de acusar "falta a foto"; se a rede cair no envio aparece 🔁 Reenviar; se a IA falhar, a foto fica guardada e os campos são preenchidos à mão.',
      'Rascunho no aparelho: o Android costuma fechar a aba ao voltar da câmera — agora as fotos já enviadas e o que foi digitado voltam sozinhos ao reabrir o formulário (por até 6 horas), com a opção "Começar do zero".',
      'A nota fiscal (impressa) passa a mandar: km, placa, motorista e nº da requisição vêm dela; a requisição manuscrita só complementa. A km da nota já entra preenchida. Leitor da requisição recalibrado com o talão real (o "SR.: Ademaldo" é o cliente, não o motorista; "KM 83003" manuscrito não é placa).',
      'Leitura por IA mais firme: tenta de novo em falha passageira (rede, tempo esgotado, serviço ocupado) e aceita a resposta mesmo com texto em volta — a segunda nota do dia 11 tinha caído por isso.',
      'Vigia do sistema (Administração ▸ 🩺 Vigia): todo sábado às 00:30 testa as dependências (IA lendo uma requisição de amostra, banco, fotos, e-mail, rastreamento, WhatsApp, o agendador), roda 20 checagens de consistência do banco (saldo, pedidos × romaneios, ferramentas, frota, obras) e a IA escreve o diagnóstico da semana com propostas de melhoria. Vai por e-mail; alerta vira push. Botão "Rodar agora".',
      'Segurança (auditoria de 12/09): o link do motorista (token do romaneio) passa a abrir só a própria entrega — antes lia e editava qualquer romaneio; ninguém mais altera os próprios módulos nem dá o módulo Administração sem ser administrador; token de romaneio gerado com gerador criptográfico; cada um só apaga a própria inscrição de avisos.',
      'Consistência: cancelar um romaneio de concerto já entregue não mexe mais nas ferramentas; "hoje" nas validades, no início dos pedidos e nas manutenções agendadas passa a ser o dia em Goiás (era o dia em UTC, adiantado 3 horas); relatório recorrente só sai em horário de expediente; falha nos alertas de óleo/pneus passa a ir para o log.',
    ],
  },
  {
    versao: 'v1.4.4',
    data: '2026-09-10',
    ambiente: 'producao',
    itens: [
      'Revisão de abastecimentos (Administração ▸ Frota ▸ Revisão) com três saídas: aprovar como está; alterar os dados (km, litros, valores, combustível, posto, requisição, nº da nota) e aprovar; ou recusar, com motivo.',
      'O recusado sai da frota: consumo e km do veículo são refeitos, a requisição e a nota ficam livres para um novo lançamento. Continua visível em "Já revisados", com quem recusou e por quê. O que foi alterado ao aprovar fica anotado no registro.',
    ],
  },
  {
    versao: 'v1.4.3',
    data: '2026-09-10',
    ambiente: 'producao',
    itens: [
      'Manutenção (Administração ▸ Frota): a nota fiscal do serviço entra por foto ou PDF — pode mais de uma, a da oficina e a das peças — e a IA lê cada uma e preenche a ficha: oficina, números das notas, data, peças, mão de obra, total, tipo e descrição. Confira e lance.',
      'Quilometragem corrigível: na ficha do veículo dá para corrigir a km atual (✏️ ao lado dela) e a km de qualquer abastecimento já lançado (✏️ na tabela). O consumo do trecho e do seguinte é refeito, o km do veículo acompanha e a correção fica registrada com quem fez e quando.',
      'Aviso de salto improvável: mais de 1.000 km desde o último abastecimento é sinal de dígito a mais (o décimo do hodômetro copiado sem a vírgula). O motorista vê o aviso ao digitar; no envio vira inconsistência — corrige, ou manda mesmo assim para a revisão da Administração. Na ficha do veículo, a coluna "Rodou" marca esses saltos.',
    ],
  },
  {
    versao: 'v1.4.2',
    data: '2026-09-08',
    ambiente: 'producao',
    itens: [
      'Leitura da requisição e da nota calibrada com os talões reais do Posto Vila Mariana: o número em vermelho ao lado de "NOTA DE VENDA Nº" agora é lido como número da requisição (antes a IA era instruída a ignorá-lo), e a nota traz "Requisição:", placa, km, motorista, veículo e obra.',
      'Km com 7 dígitos (o décimo do hodômetro copiado junto, ex. 2765813) é entendido como 276.581 km — o consumo passa a sair certo.',
      'O "Nº da nota" passa a vir do NFC-e da própria nota, não do talão.',
    ],
  },
  {
    versao: 'v1.4.1',
    data: '2026-09-08',
    ambiente: 'producao',
    itens: [
      'Abastecimento: o "ticket" passou a se chamar REQUISIÇÃO em todas as telas, mensagens e no relatório. A leitura do número em vermelho foi reforçada na foto da requisição, e a nota fiscal passa a procurá-lo como "Requisição: ####".',
    ],
  },
  {
    versao: 'v1.4.0',
    data: '2026-09-08',
    ambiente: 'producao',
    itens: [
      'Abastecimento: a comparação de combustível ficou flexível — "Diesel S-10" e "OLEO DIESEL B S10-COMUM" são o mesmo produto; só acusa quando muda a família (diesel/gasolina/etanol) ou o grau (S10/S500, comum/aditivada).',
      'A nota fiscal também alimenta km, número do ticket, placa e motorista. Quando ticket e nota concordam, entra sozinho; quando divergem, você escolhe qual vale (km e ticket). A leitura do número em vermelho do ticket foi reforçada.',
      'Inconsistência (placa ou motorista diferentes do lançamento, km/ticket que não batem com nenhum comprovante, valores divergentes) volta listada ao enviar: corrija, ou "Enviar mesmo assim" — o registro entra marcado.',
      'Administração: aviso na página inicial e contador na Frota quando há abastecimento marcado; a aba 🧾 Revisão mostra o que não bateu, com as duas fotos, para validar.',
    ],
  },
  {
    versao: 'v1.3.19',
    data: '2026-09-02',
    ambiente: 'producao',
    itens: [
      'Itens ganharam SINÔNIMOS: outros nomes e grafias erradas do produto ("alabanca", "labanca"…), separados por vírgula, no cadastro e na ficha do item. Valem em toda busca — vitrine, estoque e o casamento do Importar lista — sem aparecer em romaneio ou etiqueta.',
    ],
  },
  {
    versao: 'v1.3.18',
    data: '2026-09-02',
    ambiente: 'producao',
    itens: [
      'Cadastros de Obras e Pessoas mudaram do Escritório para a Administração (Escritório mantém Itens e Categorias); a aba Conciliação Sienge saiu da Administração.',
      'Compras ▸ Importar lista: o "🚫 Não vincular" agora vem PRIMEIRO na escolha do item, e toda linha ganhou a busca "Procurar outro item…" — inclusive as que o sistema não achou sozinho, que agora podem ser vinculadas na mão.',
    ],
  },
  {
    versao: 'v1.3.17',
    data: '2026-09-02',
    ambiente: 'producao',
    itens: [
      'A ficha de qualquer obra ganhou o botão "🏗️ Buscar no Sienge": completa a ficha com o que apareceu de novo lá (endereço, área, datas, responsável…) usando o campo "Código no Sienge" — que precisa estar preenchido; o sistema nunca adivinha o vínculo pelo código interno.',
    ],
  },
  {
    versao: 'v1.3.16',
    data: '2026-09-02',
    ambiente: 'producao',
    itens: [
      'Checar obras no Sienge: só códigos de 3 dígitos (os de 4 são outra coisa e ficam de fora), e o cadastro agora importa a FICHA — endereço, área, início, fim previsto, responsável técnico, cliente e observação, o que houver lá.',
      'Campo preenchido à mão aqui nunca é apagado por campo vazio do Sienge, e a consulta guarda memória por 10 minutos para respeitar o limite de 100 chamadas/dia.',
    ],
  },
  {
    versao: 'v1.3.15',
    data: '2026-09-02',
    ambiente: 'producao',
    itens: [
      'Sienge conectado (somente leitura): Cadastros ▸ Obras ganhou o botão "🏗️ Checar novas obras (Sienge)" — mostra as obras mais recentes do ERP que ainda não estão no sistema, já com o endereço, e cadastra com um clique. Nada é gravado no Sienge, nunca.',
    ],
  },
  {
    versao: 'v1.3.14',
    data: '2026-08-26',
    ambiente: 'producao',
    itens: [
      'O mapa do Rastreamento agora é o Google Maps — com o botão Satélite, dá para ver a obra de verdade ao acompanhar um veículo ou desenhar uma cerca.',
    ],
  },
  {
    versao: 'v1.3.13',
    data: '2026-08-26',
    ambiente: 'producao',
    itens: [
      'Administração ▸ Rastreamento: o mapa vivo dos veículos e ferramentas rastreadas, com o trajeto do dia a um clique e a configuração de cada aparelho (nome, vínculo, limite de velocidade).',
      'Cercas virtuais: desenhe um círculo ou uma área no mapa — se um rastreador sair dela, chega alerta no celular (e no WhatsApp, quando a central de Avisos tiver números configurados).',
      'Alerta de velocidade: avisa quando um veículo fica mais de 30 segundos acima do limite definido para ele.',
    ],
  },
  {
    versao: 'v1.3.12',
    data: '2026-08-25',
    ambiente: 'producao',
    itens: [
      'Começou o rastreamento por GPS: o sistema agora recebe e guarda as posições dos rastreadores instalados nos veículos e nas ferramentas (antifurto). Por enquanto é a fundação — a tela com o mapa vem na próxima versão.',
    ],
  },
  {
    versao: 'v1.3.11',
    data: '2026-08-21',
    ambiente: 'producao',
    itens: [
      'Relatório de abastecimento: coluna "Rodou" ao lado do Km — quantos km aquele abastecimento andou desde o anterior (a mesma diferença que calcula o km/l), com a soma por veículo e no total.',
    ],
  },
  {
    versao: 'v1.3.10',
    data: '2026-08-21',
    ambiente: 'producao',
    itens: [
      'Corrigido o "Enviar teste" do WhatsApp: a resposta do Zernio vinha em outro formato e o número brasileiro sem o nono dígito não casava com o digitado.',
    ],
  },
  {
    versao: 'v1.3.9',
    data: '2026-08-21',
    ambiente: 'producao',
    itens: [
      'Primeiro passo do WhatsApp: em Administração ▸ Avisos, a seção 💬 WhatsApp ativa o número de teste no seu aparelho e envia uma mensagem de teste — pelo canal OFICIAL da Meta (via Zernio), sem risco de banimento.',
    ],
  },
  {
    versao: 'v1.3.8',
    data: '2026-08-21',
    ambiente: 'producao',
    itens: [
      'Relatório de abastecimento repensado: o comum é gerado na hora (nome automático — editável —, datas de início e fim juntas) e abre direto; a lista mostra só os envios agendados.',
      'Envio recorrente com ritmo: o mês todo, de 15 em 15 dias ou semanal — sem escolher início e fim; cada e-mail cobre o ciclo que acabou de fechar.',
      'O histórico de atualizações não marca mais versão como "em teste".',
    ],
  },
  {
    versao: 'v1.3.7',
    data: '2026-08-21',
    ambiente: 'producao',
    itens: [
      'Abastecimento com dois comprovantes: foto do TICKET do posto (com o número em vermelho, que é único — o sistema recusa ticket repetido) e foto da NOTA FISCAL (hora de emissão e chave de acesso lidas sozinhas; nota repetida também é recusada).',
      'Cada foto tem dois botões: 📷 tirar foto na hora ou 🖼️ enviar da galeria do celular — antes só abria a câmera.',
      'O sistema CONFERE ticket × nota (valor, litros e produto) e aponta exatamente o que não bate. Se você insistir que o papel está certo, o registro vai para análise humana.',
      'Administração ▸ Frota ▸ 🧾 Revalidação: os comprovantes divergentes esperando um humano decidir, com as duas fotos lado a lado.',
      'Administração ▸ Frota ▸ 📊 Relatórios: relatórios de abastecimento com link público — escolha veículos, período, ordenação (por ticket, nota ou data) e o modelo de impressão (timbrado, com imagens reduzidas dos comprovantes, ou máxima economia de papel). O recorrente envia o link por e-mail todo mês, no dia que você marcar.',
    ],
  },
  {
    versao: 'v1.3.6',
    data: '2026-08-18',
    ambiente: 'producao',
    itens: [
      'Romaneio pode ser CANCELADO pelo Escritório (dentro de Editar) enquanto não foi entregue: antes da retirada só libera o estoque; em rota registra o retorno. Se veio de um pedido, você escolhe: o pedido volta para a separação ou é cancelado junto — e o solicitante é avisado.',
      'Corrigido: o botão "Iniciar separação" aparecia mas não respondia ao clique.',
      'Sandbox: o "Atualizar banco" voltou a funcionar (falhava com "must be owner of sequence").',
    ],
  },
  {
    versao: 'v1.3.5',
    data: '2026-08-14',
    ambiente: 'producao',
    itens: [
      'O sistema avisa sozinho quando há versão nova: aparece a faixa "Atualizar agora" — chega de tela antiga presa na aba aberta.',
      'Escritório ▸ Pedidos: o botão "Iniciar separação" marca o pedido como em separação (o solicitante é avisado) e só então aparece o confirmar.',
      'Escritório ▸ Pedidos: além da fila, as abas Atendidos e Cancelados mostram o histórico completo — com romaneio, entrega e motivo de recusa.',
      'A separação agora EXIGE motorista (cadastrado ou "Outro" para avulso) — romaneio não nasce mais sem dono.',
      'Romaneio pode ser editado (motorista, encarregado, observação; quantidades enquanto aguarda retirada): o card ganha a marca ✏️ e o detalhe mostra o que mudou, quando e por quem.',
    ],
  },
  {
    versao: 'v1.3.4',
    data: '2026-08-14',
    ambiente: 'producao',
    itens: [
      'O sino 🔔 no topo da tela liga os avisos deste aparelho em qualquer módulo — antes só existia no Motorista e em Meus pedidos, e quem trabalha no Escritório não tinha onde ativar.',
      'Quando o aviso não pode ser ligado, o sistema diz o motivo exato (iPhone precisa do app na Tela de Início, permissão bloqueada no navegador) em vez de "não consegui".',
      'Administração ▸ Avisos ganhou o diagnóstico "Este aparelho": mostra item por item o que falta para o aviso chegar aqui.',
      'Esta tela de histórico de atualizações, com a numeração das versões.',
    ],
  },
  {
    versao: 'v1.3.3',
    data: '2026-08-14',
    ambiente: 'producao',
    itens: [
      'Pedido novo avisa o CD inteiro no celular, e o aviso volta de hora em hora enquanto ninguém separar — só no horário de trabalho, sem almoço, fim de semana ou feriado.',
      'Administração ▸ Avisos: a central de tudo que o sistema notifica, com liga/desliga por tipo, intervalo do lembrete, quem recebe e o histórico do que foi avisado.',
    ],
  },
  {
    versao: 'v1.3.2',
    data: '2026-08-13',
    ambiente: 'producao',
    itens: [
      'Botão de imprimir o romaneio em uma página, no Escritório e em Meus pedidos, com fotos, mapa e linhas de assinatura.',
      'O mapa da entrega passou a aparecer também no histórico de romaneios do Escritório.',
    ],
  },
  {
    versao: 'v1.3.1',
    data: '2026-08-13',
    ambiente: 'producao',
    itens: [
      'Avisos de cada mudança do seu pedido: em análise, separado, saiu para entrega, entregue.',
      'Pedido entregue gera e-mail com a linha do tempo completa e o local exato da entrega.',
      'Em Meus pedidos, "Romaneio completo" mostra itens, fotos e o mapa de onde a entrega foi finalizada.',
    ],
  },
  {
    versao: 'v1.3.0',
    data: '2026-08-12',
    ambiente: 'producao',
    itens: [
      'Quem é liberado no cadastro recebe e-mail de boas-vindas com o endereço do sistema e o passo a passo dos seus módulos.',
      'Cadastros ▸ Obras: "Checar novas obras (e-mail)" busca os avisos de OBRA FECHADA e cadastra com um clique.',
      'O convite de instalar o app fala do aparelho certo (celular ou computador) e tem "não mostrar de novo".',
    ],
  },
  {
    versao: 'v1.2.0',
    data: '2026-08-11',
    ambiente: 'producao',
    itens: [
      'Instale o sistema no celular ou no computador: na primeira visita aparece o convite.',
      'Abastecimento: a foto do recibo preenche também a quilometragem, com confirmar ou corrigir.',
      'O abastecimento abre direto no seu veículo, sem escolher na lista.',
      'Motorista ⛽: consumo médio, km atual, gráfico de km rodados e histórico.',
      'Frota: troca de óleo com aviso a 1.000 km, pneus e manutenção com oficina, peças e mão de obra.',
      'Pedidos: obras mais novas primeiro, ordenação por Nome/Quantidade/Código e itens sem estoque fora da vitrine.',
    ],
  },
  {
    versao: 'v1.1.0',
    data: '2026-08-10',
    ambiente: 'producao',
    itens: [
      'As listas ordenam pelo clique no título da coluna, em todas as telas.',
      'Acompanhar pedido em Meus pedidos: separação, retirada e entrega.',
      'Ferramentas com patrimônio: empréstimo com prazo, devolução com foto e fila de concerto.',
      'Frota na Administração e abastecimento por foto do recibo no módulo do motorista.',
      'Avisos no celular para o motorista (romaneio novo e manutenção).',
    ],
  },
  {
    versao: 'v1.0.0',
    data: '2026-08-06',
    ambiente: 'producao',
    itens: [
      'Virada de chave: o Sistema CD passou a rodar por conta própria, fora da planilha.',
      'Estoque, aprovações, romaneios, pedidos, motorista e entrada por foto no sistema novo.',
    ],
  },
];

export const VERSAO_ATUAL: Versao = VERSOES[0]!;

/** A última versão que os funcionários receberam de fato. */
export const VERSAO_EM_PRODUCAO: Versao =
  VERSOES.find((v) => v.ambiente === 'producao') ?? VERSAO_ATUAL;
